import type { SessionData } from '../types';
import { MODULE_OPTIONS } from '../lib/equipment';

interface StatsPanelProps {
  data: SessionData;
  moduleId: string;
  onModuleChange: (id: string) => void;
  shadeVisible: boolean;
  onToggleShade: (visible: boolean) => void;
  onToggleRemoveMode: () => void;
  removeMode: boolean;
  onExport: () => void;
  onConsumptionChange: (kwh: number | null) => void;
  consumptionEnabled: boolean;
  onToggleConsumption: (enabled: boolean) => void;
  onSave: () => void;
  saveState: 'idle' | 'saving' | 'saved' | 'error';
}

export function StatsPanel({
  data,
  moduleId,
  onModuleChange,
  shadeVisible,
  onToggleShade,
  onToggleRemoveMode,
  removeMode,
  onExport,
  onConsumptionChange,
  consumptionEnabled,
  onToggleConsumption,
  onSave,
  saveState,
}: StatsPanelProps) {
  const mod = data.panel_layout.equipment.module;
  const isEstimated = data.production.source === 'estimated';

  const sourceBadge = (() => {
    switch (data.roof_model.source) {
      case 'google_solar':
        return { label: '🛰️ Google Solar API — real roof data', color: '#22c55e' };
      case 'traced':
        return { label: '✏️ Manually traced roof', color: '#38bdf8' };
      default:
        return { label: '🧪 Simulated roof (generic)', color: '#94a3b8' };
    }
  })();

  const shadingBasis = (() => {
    switch (data.roof_model.shading_basis) {
      case 'measured':
        return 'Shading: measured (Google sunshine data)';
      case 'modeled':
        return 'Shading: modeled from obstructions';
      default:
        return 'Shading: heuristic estimate';
    }
  })();

  return (
    <div className="stats-panel">
      <div className="stats-header">Design Summary</div>

      <div className="data-source-badge" style={{ color: sourceBadge.color, borderColor: sourceBadge.color }}>
        {sourceBadge.label}
      </div>
      <div style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 8px' }}>{shadingBasis}</div>

      <div className="stat-card">
        <div className="stat-label">Annual Production</div>
        <div className="stat-value">
          {data.production.annual_kwh.toLocaleString()}
          <span className="stat-unit">kWh/yr</span>
          {isEstimated && <span style={{ fontSize: 10, color: '#f59e0b', marginLeft: 6 }}>~est.</span>}
          {data.production.source === 'google_solar' && <span style={{ fontSize: 10, color: '#22c55e', marginLeft: 6 }}>real</span>}
        </div>
      </div>

      <div className="stat-card">
        <div className="stat-label">System Size</div>
        <div className="stat-value">
          {data.panel_layout.total_system_kw.toFixed(2)}
          <span className="stat-unit">kW</span>
        </div>
      </div>

      <div className="stats-section-title">Design Details</div>
      <div className="stat-row">
        <span className="stat-row-label">Address</span>
        <span className="stat-row-value">{data.address.length > 25 ? data.address.substring(0, 25) + '...' : data.address}</span>
      </div>
      <div className="stat-row">
        <span className="stat-row-label">Panel Count</span>
        <span className="stat-row-value">{data.panel_layout.panels.length}</span>
      </div>
      <div className="stat-row">
        <span className="stat-row-label">Roof Faces Used</span>
        <span className="stat-row-value">{data.roof_model.faces.filter(f => f.solar_access >= 60).length}</span>
      </div>
      <div className="stat-row">
        <span className="stat-row-label">Total Roof Area</span>
        <span className="stat-row-value">{Math.round(data.roof_model.faces.reduce((s, f) => s + f.area_sqft, 0))} sqft</span>
      </div>
      <div className="stat-row">
        <span className="stat-row-label">Panel Wattage</span>
        <span className="stat-row-value">{mod.wattage}W</span>
      </div>
      <div className="stat-row">
        <span className="stat-row-label">Panel Efficiency</span>
        <span className="stat-row-value">{mod.efficiency}%</span>
      </div>

      <div className="stats-section-title">Equipment</div>
      <select
        className="equipment-select"
        value={moduleId}
        onChange={(e) => onModuleChange(e.target.value)}
      >
        {MODULE_OPTIONS.map(m => (
          <option key={m.id} value={m.id}>
            {m.manufacturer} {m.model} ({m.wattage}W, {m.efficiency}%)
          </option>
        ))}
      </select>

      <div className="stats-section-title">Options</div>
      <label className="toggle-label">
        <input type="checkbox" checked={shadeVisible} onChange={(e) => onToggleShade(e.target.checked)} />
        Show shade map
      </label>
      <label className="toggle-label">
        <input
          type="checkbox"
          checked={consumptionEnabled}
          onChange={(e) => onToggleConsumption(e.target.checked)}
        />
        Enter annual consumption
      </label>
      {consumptionEnabled && (
        <div>
          <input
            type="number"
            className="address-input"
            style={{ width: '100%', marginTop: 8, padding: 8 }}
            placeholder="Annual kWh (e.g., 12000)"
            onChange={(e) => onConsumptionChange(e.target.value ? parseFloat(e.target.value) : null)}
          />
          <div className="stat-row" style={{ marginTop: 8 }}>
            <span className="stat-row-label">Production Offset</span>
            <span className="stat-row-value">
              {data.production.offset_pct !== null ? `${data.production.offset_pct.toFixed(0)}%` : '—'}
            </span>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button
          className={`btn-remove-mode ${removeMode ? 'active' : ''}`}
          onClick={onToggleRemoveMode}
        >
          {removeMode ? 'Click panels to remove' : 'Remove panels'}
        </button>
        <button className="btn-export" onClick={onExport}>
          Export JSON
        </button>
      </div>
      <button
        className="btn-design"
        style={{ width: '100%', marginTop: 8 }}
        onClick={onSave}
        disabled={saveState === 'saving'}
      >
        {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓' : saveState === 'error' ? 'Save failed — retry' : '💾 Save Design'}
      </button>
    </div>
  );
}
